<template>
  <Transition name="cookie-banner-slide">
    <div 
      v-if="showBanner" 
      class="cookie-sidebar-wrapper"
      role="dialog"
      aria-label="Cookie consent"
      aria-modal="true"
    >
      <!-- Backdrop que bloquea interacción -->
      <div class="cookie-backdrop"></div>
      
      <!-- Panel lateral -->
      <div class="cookie-sidebar">
        <!-- Logo -->
        <div class="cookie-logo">
          <img src="@/assets/images/logo.svg" alt="Poseidonie" class="logo-image" />
        </div>

        <!-- Contenido -->
        <div class="cookie-content">
          <h3 class="heading-4 cookie-title">{{ $t('cookies.title') }}</h3>
          <p class="body-text-sm cookie-text">{{ $t('cookies.description') }}</p>
        </div>

        <!-- Botones -->
        <div class="cookie-actions">
          <button 
            @click="handleReject" 
            class="btn-architectural cookie-btn-secondary"
            aria-label="Reject cookies"
          >
            {{ $t('cookies.reject') }}
          </button>
          <button 
            @click="handleAccept" 
            class="btn-architectural cookie-btn-primary"
            aria-label="Accept cookies"
          >
            {{ $t('cookies.accept') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useCookieConsent } from '@/composables/general/useCookieConsent'

const { showBanner, acceptCookies, rejectCookies } = useCookieConsent()

const handleAccept = () => {
  acceptCookies()
}

const handleReject = () => {
  rejectCookies()
}
</script>

<style scoped>
/* ============================================
   COOKIE BANNER - SIDEBAR LATERAL
   Panel deslizante desde la derecha
   ============================================ */

/* Wrapper principal */
.cookie-sidebar-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  justify-content: flex-end;
}

/* Backdrop semitransparente */
.cookie-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

/* Panel lateral */
.cookie-sidebar {
  position: relative;
  width: 100%;
  max-width: 420px;
  height: 100%;
  background: rgb(255, 255, 255);
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  padding: 2.5rem 2rem;
  gap: 2rem;
  overflow-y: auto;
}

/* Logo */
.cookie-logo {
  width: 100%;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  opacity: 0.5;
}

.logo-image {
  height: 100%;
  width: auto;
  object-fit: contain;
  display: block;
}

/* Contenido */
.cookie-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Título */
.cookie-title {
  color: rgb(0, 0, 0) !important;
  margin: 0 !important;
  text-align: left !important;
}

/* Texto descriptivo */
.cookie-text {
  color: rgb(74, 74, 74) !important;
  margin: 0 !important;
  line-height: 1.7 !important;
  text-align: left !important;
}

/* Botones */
.cookie-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  margin-top: auto;
}

/* Forzar colores modo light para botones */
.cookie-actions .btn-architectural {
  width: 100%;
  justify-content: center;
}

.cookie-btn-primary {
  background-color: rgb(0, 0, 0) !important;
  color: rgb(255, 255, 255) !important;
  border-color: rgb(0, 0, 0) !important;
}

.cookie-btn-primary:hover {
  background-color: rgb(42, 42, 42) !important;
  color: rgb(255, 255, 255) !important;
}

.cookie-btn-secondary {
  background-color: transparent !important;
  color: rgb(0, 0, 0) !important;
  border-color: rgb(0, 0, 0) !important;
}

.cookie-btn-secondary:hover {
  background-color: rgb(0, 0, 0) !important;
  color: rgb(255, 255, 255) !important;
  border-color: rgb(0, 0, 0) !important;
}

/* Transiciones - Slide desde la derecha */
.cookie-banner-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.cookie-banner-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.cookie-banner-slide-enter-from .cookie-backdrop,
.cookie-banner-slide-leave-to .cookie-backdrop {
  opacity: 0;
}

.cookie-banner-slide-enter-from .cookie-sidebar {
  transform: translateX(100%);
}

.cookie-banner-slide-leave-to .cookie-sidebar {
  transform: translateX(100%);
}

/* Responsive Design */
@media (max-width: 1023px) {
  .cookie-sidebar-wrapper {
    align-items: flex-end;
  }

  .cookie-sidebar {
    max-width: 100%;
    height: auto;
    border-radius: 16px 16px 0 0;
    padding: 2rem 1.5rem;
    gap: 1.5rem;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
  }

  .cookie-logo {
    height: 16px;
  }

  /* Animación desde abajo en mobile/tablet */
  .cookie-banner-slide-enter-from .cookie-sidebar {
    transform: translateY(100%);
  }

  .cookie-banner-slide-leave-to .cookie-sidebar {
    transform: translateY(100%);
  }
}

/* Tablet landscape y desktop */
@media (min-width: 1024px) {
  .cookie-sidebar {
    padding: 3rem 2.5rem;
  }
}

/* Dark mode - El banner SIEMPRE mantiene el tema light */
/* No hay ajustes necesarios porque usamos colores fijos */

/* Soporte para movimiento reducido */
@media (prefers-reduced-motion: reduce) {
  .cookie-banner-fade-enter-active,
  .cookie-banner-fade-leave-active,
  .cookie-btn {
    transition: none;
  }

  .cookie-btn-primary:hover,
  .cookie-btn-secondary:hover {
    transform: none;
  }
}
</style>
